/**
 * One rule, stated three ways: an item and its timer agree.
 *
 * - Check an item off, and the timer running against it ends.
 * - End a timer, and the item it was running against is checked off.
 * - Canvas notices a submission, and both of the above happen at once.
 *
 * They live here rather than at the three call sites that need them because
 * as three call sites they would drift. The second one written forgets
 * something the first one did, and the disagreement shows up on screen as a
 * ticked row with a clock still running against it -- which is exactly the
 * state this whole feature exists to make impossible.
 *
 * ## The translation everything here depends on
 *
 * `session.workItemId` is NOT an item key, and nothing about the two spellings
 * makes that obvious:
 *
 *   the agenda starts a session with  `canvas:90210`
 *   the desktop starts one with       `118`
 *   today's list keys those rows      `c90210` and `w118`
 *
 * Get the translation wrong and nothing throws. The flag is simply written
 * under a name no row will ever ask about, so the tick never appears, the
 * timer never ends, and there is no error anywhere to explain why.
 *
 * ## Failure is partial on purpose
 *
 * Each agreement is two writes to two different places, and a phone loses
 * signal between them. Nothing here throws: whichever half reached the server
 * is kept, and the caller is told what happened. Refusing to end a clock
 * because a flag did not save would be the worst of both -- the work looks
 * unfinished AND the timer keeps counting.
 */

/**
 * The item key a session is running against, or null if it is a break.
 *
 * `canvas:90210` is the agenda's spelling and `118` is the desktop's. An
 * unscheduled session has neither, which is the whole difference between a
 * break and work on something -- and why ending one ticks nothing off.
 */
export function keyForSession(session) {
  const raw = session?.workItemId;
  if (raw === null || raw === undefined || raw === '') return null;
  const text = String(raw);
  if (text.startsWith('canvas:')) {
    const id = text.slice('canvas:'.length);
    return id ? `c${id}` : null;
  }
  return `w${text}`;
}

const isLive = (session) => Boolean(session) && session.state !== 'done';

/** End `live` if it is running against any of `keys`. */
async function endIfItIsAbout(deps, config, session, live, keys) {
  if (!isLive(live)) return false;
  const running = keyForSession(live);
  if (running === null || !keys.includes(running)) return false;
  try {
    await deps.endSession(config, session, live.sessionId, null);
    return true;
  } catch {
    return false;
  }
}

async function mark(deps, config, session, key, source) {
  try {
    await deps.markDone(config, session, key, source);
    return true;
  } catch {
    return false;
  }
}

/**
 * A thumb on a checkbox.
 *
 * Both halves are attempted regardless of whether the other worked, because
 * they fail independently and each is worth having on its own.
 */
export async function tickOff(deps, config, session, { key, live, canvasKey } = {}) {
  if (!key) return { marked: false, ended: false };
  const keys = [key, canvasKey].filter(Boolean);
  const [marked, ended] = await Promise.all([
    mark(deps, config, session, key, 'checked'),
    endIfItIsAbout(deps, config, session, live, keys),
  ]);
  return { marked, ended };
}

/**
 * Unticking.
 *
 * Clears the flag and nothing else. It deliberately does not start a timer:
 * a timer that restarts itself would invent minutes nobody worked.
 */
export async function untick(deps, config, session, { key } = {}) {
  if (!key) return { cleared: false };
  try {
    await deps.clearDone(config, session, key);
    return { cleared: true };
  } catch {
    return { cleared: false };
  }
}

/**
 * A timer that has just ended.
 *
 * Marks only -- it never ends anything, which is what stops the two
 * directions of this rule calling each other round in a circle. The session
 * is already over by the time this runs.
 */
export async function onSessionEnded(deps, config, session, ended) {
  const key = keyForSession(ended);
  if (key === null) return { marked: false };
  return { marked: await mark(deps, config, session, key, 'timer') };
}

/**
 * Canvas saying a piece of work was submitted.
 *
 * `source: 'canvas'` and never `'checked'`: "Canvas says you submitted it" and
 * "you ticked it" are different facts, and a record that flattens them cannot
 * explain itself later.
 *
 * `canvasKey` is accepted alongside `key` because the same work can be ticked
 * as a block (`w118`) while being timed from the agenda (`canvas:90210`).
 * Passing both means the timer ends whichever way round it was started.
 */
export async function onCanvasSubmitted(deps, config, session, { key, live, canvasKey } = {}) {
  if (!key) return { marked: false, ended: false };
  const keys = [key, canvasKey].filter(Boolean);
  const [marked, ended] = await Promise.all([
    mark(deps, config, session, key, 'canvas'),
    endIfItIsAbout(deps, config, session, live, keys),
  ]);
  return { marked, ended };
}
